import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Modal, Button, Toast, ToastContainer } from 'react-bootstrap';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    resolve: null
  });

  const notify = useCallback((options) => {
    const toast = {
      id: Date.now() + Math.random(),
      variant: options.variant || 'info',
      title: options.title || '',
      message: options.message || '',
      delay: options.delay ?? 3500
    };

    setToasts((prev) => [...prev, toast]);

    if (toast.delay > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((item) => item.id !== toast.id));
      }, toast.delay);
    }
  }, []);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmState({
        show: true,
        title: options.title || 'Confirm',
        message: options.message || '',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        resolve
      });
    });
  }, []);

  const handleConfirm = (confirmed) => {
    if (confirmState.resolve) {
      confirmState.resolve(confirmed);
    }
    setConfirmState((prev) => ({
      ...prev,
      show: false,
      resolve: null
    }));
  };

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  return (
    <NotificationContext.Provider value={value}>
      {children}

      <ToastContainer position="top-end" className="p-3 toast-under-nav">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            bg={toast.variant}
            onClose={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
            delay={toast.delay}
            autohide={toast.delay > 0}
          >
            {toast.title && (
              <Toast.Header>
                <strong className="me-auto">{toast.title}</strong>
              </Toast.Header>
            )}
            <Toast.Body>{toast.message}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>

      <Modal show={confirmState.show} onHide={() => handleConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmState.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmState.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => handleConfirm(false)}>
            {confirmState.cancelText}
          </Button>
          <Button variant="primary" onClick={() => handleConfirm(true)}>
            {confirmState.confirmText}
          </Button>
        </Modal.Footer>
      </Modal>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
